import React, { useState } from "react";
import styled from "styled-components";
import { FaPlus, FaMinus, FaShoppingBasket } from "react-icons/fa";
import { useCart } from "../../subjects/CartContext";
import { thousands_separators } from "../../utils/formatCurrency";
import { getProductImage, ACCENT, TEAL, INK, MUTED, LINE } from "../products/Products";

const ExpressProductCard = ({ product }) => {
  const { items, addItemToCart } = useCart();
  const inCart = items?.find((p) => p?.product.id === product.id);
  const [qty, setQty] = useState(inCart?.qty || 1);
  const outOfStock = product.quantity_available_for_sale <= 0;
  const hasDiscount = !!product.discount_amount;
  const image = product?.product_images?.length ? product.product_images[0].url : getProductImage(product.display_name);

  const clamp = (n) => Math.max(1, Math.min(n, product.quantity_available_for_sale || 1));

  return (
    <Card $disabled={outOfStock}>
      <ImageWrap>
        <img src={image} alt={product.display_name || product.name} loading="lazy" />
        {hasDiscount && <DiscountBadge>-{Math.round((product.discount_amount / product.unit_value) * 100)}%</DiscountBadge>}
        {outOfStock && <OosBadge>Out of stock</OosBadge>}
        {inCart && (
          <InCartBadge>
            <FaShoppingBasket size={10} /> {inCart.qty}
          </InCartBadge>
        )}
      </ImageWrap>

      <Body>
        <Name>{product.display_name || product.name}</Name>
        <PriceRow>
          <Price>KSh {thousands_separators(product.discounted_price ?? product.unit_value)}</Price>
          {hasDiscount && <WasPrice>KSh {thousands_separators(product.unit_value)}</WasPrice>}
        </PriceRow>

        {!outOfStock && (
          <Footer>
            <Stepper>
              <StepBtn onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1}>
                <FaMinus size={9} />
              </StepBtn>
              <QtyVal>{qty}</QtyVal>
              <StepBtn onClick={() => setQty((q) => clamp(q + 1))} disabled={qty >= (product.quantity_available_for_sale || 1)}>
                <FaPlus size={9} />
              </StepBtn>
            </Stepper>
            <AddBtn onClick={() => addItemToCart(product, qty)}>
              <FaShoppingBasket size={12} />
              {inCart ? "Update" : "Add"}
            </AddBtn>
          </Footer>
        )}
      </Body>
    </Card>
  );
};

export default ExpressProductCard;

const Card = styled.div`
  background: #fff;
  border-radius: 1.1rem;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(36, 28, 64, 0.07);
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  display: flex;
  flex-direction: column;
`;
const ImageWrap = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  background: linear-gradient(180deg, #f8f7fc 0%, #f1f0f8 100%);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
const DiscountBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
`;
const OosBadge = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(36, 28, 64, 0.55);
  color: #fff;
  font-weight: 800;
  font-size: 0.75rem;
`;
const InCartBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: ${TEAL};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
`;
const Body = styled.div`
  padding: 0.7rem 0.75rem 0.8rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Name = styled.div`
  font-weight: 700;
  font-size: 0.8rem;
  color: ${INK};
  line-height: 1.25;
  min-height: 2.1em;
`;
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin: 0.3rem 0 0.6rem;
`;
const Price = styled.span`
  font-weight: 800;
  font-size: 0.95rem;
  color: ${ACCENT};
`;
const WasPrice = styled.span`
  font-size: 0.72rem;
  color: ${MUTED};
  text-decoration: line-through;
`;
const Footer = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;
const Stepper = styled.div`
  display: flex;
  align-items: center;
  border: 1.5px solid ${LINE};
  border-radius: 999px;
  padding: 0.15rem;
`;
const StepBtn = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #f6f5fc;
  color: ${ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;
const QtyVal = styled.span`
  width: 22px;
  text-align: center;
  font-weight: 700;
  font-size: 0.78rem;
  color: ${INK};
`;
const AddBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  background: ${ACCENT};
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.45rem 0.5rem;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
`;